import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1706878481007-eec2c5e8133c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgzNzYwNDA3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError("Credenziali non valide. Controlla di aver inserito l'email completa (es. nome@dominio.it) e la password esatta, rispettando maiuscole e simboli.");
      } else if (!err.response || status >= 500) {
        setError("Servizio momentaneamente non raggiungibile. Attendi qualche secondo e riprova.");
      } else {
        setError(apiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A1826]">
      {/* Left form */}
      <div className="w-full lg:w-[46%] flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <img src="/rebreton-logo-bianco.webp" alt="Rebreton Real Estate" className="h-11 w-auto object-contain object-left" />
            <div className="text-[11px] text-[#EA7B2B] uppercase tracking-[0.25em] mt-3 pl-1">Beta Aste</div>
          </div>

          <h1 className="text-3xl font-light text-white mb-2">Bentornato.</h1>
          <p className="text-sm text-zinc-500 mb-8">
            Accedi al motore di origination delle operazioni immobiliari distressed.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5 w-full bg-[#122A3F] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] focus:border-[#EA7B2B] transition-colors"
                required
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5 w-full bg-[#122A3F] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] focus:border-[#EA7B2B] transition-colors"
                required
              />
            </div>

            {error && (
              <div className="text-sm text-[#E11D48] bg-[#E11D48]/10 border border-[#E11D48]/30 rounded-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#EA7B2B] text-black font-medium py-3 rounded-sm hover:bg-[#F59E4A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Accedi
            </button>

            <div className="text-center pt-1">
              <Link to="/recupera-password" data-testid="forgot-password-link" className="text-sm text-zinc-500 hover:text-[#EA7B2B] transition-colors">
                Password dimenticata?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-xs text-zinc-600 border-t border-white/5 pt-4" data-testid="login-hint">
            Ambiente di beta test · Accesso riservato agli utenti autorizzati
          </div>
        </div>
      </div>

      {/* Right image */}
      <div className="hidden lg:block flex-1 relative">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1826] via-[#0A1826]/40 to-transparent" />
        <div className="absolute bottom-12 right-12 left-12 text-right">
          <p className="font-display text-2xl font-light text-white/90 leading-snug">
            Trovare, leggere, calcolare,<br />qualificare e prioritizzare.
          </p>
          <p className="text-sm text-[#EA7B2B] mt-3">Ogni giorno · Su tutta Italia · In automatico</p>
        </div>
      </div>
    </div>
  );
}
